const parser = typeof DOMParser !== "undefined" ? new DOMParser() : null;

/**
 * Strips markup from a rich-text value and returns the plain text.
 *
 * Uses DOMParser rather than assigning to innerHTML: an inert document does
 * not run `<img onerror>` and does not fetch the resources it references,
 * which matters because the input is stored content authored elsewhere.
 */
export function htmlToText(html) {
    if (!html) {
        return "";
    }

    const value = String(html);

    if (!parser) {
        return value.replace(/<[^>]*>/g, "").trim();
    }

    const parsed = parser.parseFromString(value, "text/html");
    return (parsed.body.textContent || "").trim();
}

const RICH_TEXT_TAGS = ["P", "BR", "STRONG", "B", "EM", "I", "U", "S", "UL", "OL", "LI", "H1", "H2", "H3", "H4", "BLOCKQUOTE", "SPAN"];
const DROPPED_TAGS = ["SCRIPT", "STYLE", "IFRAME", "OBJECT", "EMBED", "TEMPLATE", "SVG", "MATH"];

function escapeText(text) {
    return String(text).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function sanitizeNode(node) {
    if (node.nodeType === 3) {
        return escapeText(node.textContent);
    }

    if (node.nodeType !== 1 || DROPPED_TAGS.includes(node.tagName)) {
        return "";
    }

    const children = Array.from(node.childNodes).map(sanitizeNode).join("");

    if (!RICH_TEXT_TAGS.includes(node.tagName)) {
        return children;
    }

    const tag = node.tagName.toLowerCase();
    return tag === "br" ? "<br>" : `<${tag}>${children}</${tag}>`;
}

export function sanitizeRichText(html) {
    if (!html || !parser) {
        return "";
    }

    const parsed = parser.parseFromString(String(html), "text/html");
    return Array.from(parsed.body.childNodes).map(sanitizeNode).join("");
}

export default { htmlToText, sanitizeRichText };
